import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, CalendarPlus, MessageCircle, Navigation, Loader2, BadgeIndianRupee, AlertCircle } from "lucide-react";
import { api, fmtDate, recallPhone } from "@/lib/api";
import { ADDRESS_FULL, MAPS_DIR_URL, PHONE_DISPLAY } from "@/lib/clinic";

const pad = (n) => String(n).padStart(2, "0");
const calendarUrl = (b) => {
  const [h, m] = b.slot_time.split(":").map(Number);
  const end = h * 60 + m + b.slot_duration_mins;
  const d = b.appointment_date.replaceAll("-", "");
  const dates = `${d}T${pad(h)}${pad(m)}00/${d}T${pad(Math.floor(end / 60))}${pad(end % 60)}00`;
  const q = new URLSearchParams({
    action: "TEMPLATE", text: "Dental appointment – Smileton Dental", dates, ctz: "Asia/Kolkata",
    details: `Booking ref ${b.booking_ref}\n${b.visit_reason_title}\nDr. Vilayath Mahamadi\nClinic: ${PHONE_DISPLAY}`, location: ADDRESS_FULL,
  });
  return `https://calendar.google.com/calendar/render?${q}`;
};
const whatsappUrl = (b, phone) => {
  const msg = `Smileton Dental appointment confirmed ✅\nRef: ${b.booking_ref}\n${fmtDate(b.appointment_date)} at ${b.slot_label}\n${b.visit_reason_title}\nFee paid: ₹${b.amount_paid}\n📍 2nd Floor, Shivam Tejas Building, Above Ratnadeep Supermarket, Sainikpuri\nDirections: ${MAPS_DIR_URL}\nClinic: ${PHONE_DISPLAY}`;
  return phone ? `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
};

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
    <span className="text-ink-muted text-sm">{label}</span>
    <span className="text-ink font-medium text-sm text-right" data-testid={testid}>{value}</span>
  </div>
);

const Action = ({ href, icon: I, children, testid, primary }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
    className={`h-12 rounded-full inline-flex items-center justify-center gap-2 font-semibold text-sm transition-colors ${primary ? "bg-teal text-white hover:bg-teal-dark" : "border border-slate-200 bg-white text-ink hover:bg-mint"}`}>
    <I className="h-4 w-4" />{children}
  </a>
);

export default function Confirmation() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="confirmation-not-found"><div><AlertCircle className="h-8 w-8 mx-auto text-red-500" /><p className="mt-3">We couldn't find this booking.</p><Link to="/" className="text-teal underline">Back to home</Link></div></div>;
  if (!b) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;
  const ok = ["CONFIRMED", "COMPLETED"].includes(b.status);

  return (
    <div className="min-h-screen bg-alabaster grain py-10 px-4" data-testid="confirmation-page">
      <div className="max-w-lg mx-auto reveal">
        <Link to="/" className="flex items-center justify-center gap-2 font-display text-xl text-teal mb-6"><img src="/images/logo.png" alt="" className="h-10 w-10 rounded-full" />Smileton Dental</Link>
        <div className="rounded-[2rem] bg-white border border-mint-border/70 shadow-[0_30px_60px_-30px_rgba(15,76,92,0.45)] p-6 sm:p-8">
          {ok ? (
            <div className="text-center">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
              <h1 className="mt-3 text-3xl sm:text-4xl font-semibold text-ink" data-testid="confirmation-title">You're booked!</h1>
              <p className="text-ink-muted mt-1 text-sm">See you soon, {b.patient_name.split(" ")[0]}.</p>
            </div>
          ) : (
            <div className="text-center" data-testid="confirmation-not-confirmed">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto" />
              <h1 className="mt-3 text-2xl font-semibold text-ink">{b.status === "PENDING_PAYMENT" ? "Waiting for payment" : "This booking is not active"}</h1>
              <p className="text-ink-muted mt-1 text-sm">Please book again or call {PHONE_DISPLAY}.</p>
            </div>
          )}
          <div className="mt-6 rounded-2xl bg-mint/60 border border-mint-border p-4 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-teal-light">Booking reference</p>
            <p className="font-display text-3xl font-semibold text-teal tracking-wider" data-testid="booking-ref">{b.booking_ref}</p>
          </div>
          <div className="mt-4">
            <Row label="Date" value={fmtDate(b.appointment_date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })} testid="confirmation-date" />
            <Row label="Time" value={b.slot_label} testid="confirmation-time" />
            <Row label="Visit reason" value={b.visit_reason_title} testid="confirmation-reason" />
            <Row label="Doctor" value="Dr. Vilayath Mahamadi" />
          </div>
          {ok && (
            <div className="mt-4 flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="paid-receipt-badge">
              <span className="inline-flex items-center gap-2 text-emerald-700 font-semibold"><BadgeIndianRupee className="h-5 w-5" />₹{b.amount_paid} Paid</span>
              <span className="text-[11px] text-emerald-700/80 truncate max-w-[50%]">{b.razorpay_payment_id}</span>
            </div>
          )}
          {ok && (
            <div className="mt-6 grid gap-2.5">
              <Action href={calendarUrl(b)} icon={CalendarPlus} testid="add-to-calendar-button" primary>Add to Google Calendar</Action>
              <Action href={whatsappUrl(b, recallPhone(b.id))} icon={MessageCircle} testid="whatsapp-share-button">Send Details to My WhatsApp</Action>
              <Action href={MAPS_DIR_URL} icon={Navigation} testid="directions-button">Get Directions (Above Ratnadeep, Sainikpuri)</Action>
            </div>
          )}
        </div>
        <Link to={`/manage?ref=${b.booking_ref}`} className="block text-center text-sm text-ink-muted mt-6 hover:text-teal" data-testid="manage-booking-link">Need to reschedule? Manage booking</Link>
        <Link to="/" className="block text-center text-sm text-teal mt-3 hover:underline" data-testid="back-home-link">Back to Smileton Dental</Link>
      </div>
    </div>
  );
}
